// #PAYROLL_JOBS_V1 (2026-10-22) — Bill: "be nice to have this spend on the CC table more accurate. Maybe load a week and it
// pull from Payroll data?" When payroll is loaded in RU, the week's time-card lines are kept as WHO worked WHICH job on
// WHICH day (hours + job only — the payroll file has no pay in it, and none is stored). Card Spend then codes each card charge
// the way Bill does by hand: the cardholder's own clock-in that day; supervisors who aren't clocked by name follow their
// crew's login; shop time → Co? DDD; office / owners / estimating → Overhead. A typed DUC/Unit No. always wins.
// Storage: app_storage 'payroll-jobs-<week-ending Sunday ISO>' = { v, weekEnd, at, people:{ "Last, First": { iso: { duc: hrs } } }, logins:{ login: { iso: { duc: hrs } } } }
export const PAYJOBS_KEY = (weekEndISO) => `payroll-jobs-${weekEndISO}`;

// Card name (as on the bank extract) → the crew login whose clock-ins carry his job (he isn't clocked by name).
export const CARD_VIA_LOGIN = { 'ED SOTO': 'esoto', 'NORBERTO ESPINO': 'nespino', 'NOEL SOTO': 'npena', 'MARK WATSON': 'jpadilla' };
// Fixed homes from Bill's sheet.
export const CARD_FIXED = { 'ROBERT MACY': { co: 'DDD' }, 'JERRY WAEDEKIN': { co: 'DDD' }, 'GERALD BENSON': { duc: 'RANCH', note: 'RANCH' } };
// Left blank on purpose in Bill's sheet = overhead (owners, office, estimating).
export const CARD_OVERHEAD = ['ASHLIE GRAFF', 'AARON GRAFF', 'BILL PAYNE', 'CHASE VARNER', 'KEVIN BAKER', 'MARK THOMPSON'];

const p2 = (n) => String(n).padStart(2, '0');
// "10/15" (+ the week's start/end ISO for the year) → "2026-10-15"
const dateOf = (s, weekStartISO, weekEndISO) => {
  const m = String(s || '').trim().match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/); if (!m) return '';
  let y = m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : +String(weekEndISO).slice(0, 4);
  if (!m[3] && +m[1] === 12 && String(weekEndISO).slice(5, 7) === '01') y -= 1;   // a Dec day in a week that ends in Jan
  return `${y}-${p2(m[1])}-${p2(m[2])}`;
};

// lines: the payroll tab as rows of cells; H: lower-cased header row. Returns the record to store.
export function buildPayrollJobs(lines, H, weekStartISO, weekEndISO) {
  const ix = (n) => H.findIndex(h => h === n || h.startsWith(n));
  const iE = ix('employee'), iD = H.findIndex(h => h === 'duc'), iW = ix('work date'), iH = ix('hours'), iL = ix('entered by');
  const people = {}, logins = {}, crews = {};   // #BIGRIG_AUTO_V1 — crews: login → iso → [names] (who that SUP clocked that day)
  const add = (o, a, d, duc, h) => { const x = (o[a] = o[a] || {}); const y = (x[d] = x[d] || {}); y[duc] = Math.round(((y[duc] || 0) + h) * 100) / 100; };
  lines.forEach(c => {
    const emp = String(c[iE] || '').trim(); const duc = String(c[iD] || '').trim(); const d = dateOf(c[iW], weekStartISO, weekEndISO);
    if (!emp || !duc || !d) return;
    const h = Number(String(c[iH] || '').replace(/[^0-9.\-]/g, '')) || 0;
    add(people, emp, d, duc, h);
    const lg = String(c[iL] || '').trim().toLowerCase(); if (lg) { add(logins, lg, d, duc, h); const cl = (crews[lg] = crews[lg] || {}); const ar = (cl[d] = cl[d] || []); if (!ar.includes(emp)) ar.push(emp); }
  });
  return { v: 2, weekEnd: weekEndISO, at: new Date().toISOString(), people, logins, crews };
}

// #BIGRIG_AUTO_V1 (2026-10-28) — same record from the Payroll Library's parsed workbook lines ({ emp, date ISO, duc, hrs, by }),
// so ONE drop of the weekly .xlsx (Payroll → Library) feeds Card Spend auto-coding and the Big Rig perk crews too.
export function buildPayrollJobsFromLines(lines, weekEndISO) {
  const people = {}, logins = {}, crews = {};
  const add = (o, a, d, duc, h) => { const x = (o[a] = o[a] || {}); const y = (x[d] = x[d] || {}); y[duc] = Math.round(((y[duc] || 0) + h) * 100) / 100; };
  (lines || []).forEach(l => {
    const emp = String(l.emp || '').trim(), duc = String(l.duc || '').trim(), d = String(l.date || '').slice(0, 10);
    if (!emp || !duc || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
    const h = Number(l.hrs) || 0; add(people, emp, d, duc, h);
    const lg = String(l.by || '').trim().toLowerCase(); if (lg) { add(logins, lg, d, duc, h); const cl = (crews[lg] = crews[lg] || {}); const ar = (cl[d] = cl[d] || []); if (!ar.includes(emp)) ar.push(emp); }
  });
  return { v: 2, weekEnd: weekEndISO, at: new Date().toISOString(), people, logins, crews, from: 'library' };
}

const top = (byDuc) => Object.entries(byDuc || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
const pickDay = (days, iso) => {
  const ks = Object.keys(days || {}); if (!ks.length) return null;
  if (days[iso]) return { duc: top(days[iso]), exact: true };
  const t = new Date(iso + 'T12:00:00').getTime();
  const near = ks.sort((a, b) => Math.abs(new Date(a + 'T12:00:00') - t) - Math.abs(new Date(b + 'T12:00:00') - t) || a.localeCompare(b))[0];
  return { duc: top(days[near]), exact: false, near };
};
const homeOf = (duc) => /^shop/i.test(duc) ? { co: 'DDD' } : /^office/i.test(duc) ? { overhead: true } : /^training/i.test(duc) ? { overhead: true } : { duc };

// Card name "0879 - MANUEL ESPINO" + charge date → { duc?, co?, note?, overhead?, how } or null (nothing to go on).
export function codeCardCharge(pj, card, iso) {
  const full = String(card || '').replace(/^\s*\d+\s*-\s*/, '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!full) return null;
  if (CARD_FIXED[full]) return { ...CARD_FIXED[full], how: 'fixed' };
  if (CARD_OVERHEAD.includes(full)) return { overhead: true, how: 'overhead' };
  if (!pj) return null;
  if (CARD_VIA_LOGIN[full]) { const p = pickDay(pj.logins && pj.logins[CARD_VIA_LOGIN[full]], iso); if (p) return { ...homeOf(p.duc), how: p.exact ? 'crew' : 'crew~' }; }
  const parts = full.split(' '); const first = parts[0], last = parts[parts.length - 1];
  const emp = Object.keys(pj.people || {}).find(e => { const [l, f] = e.split(',').map(s => String(s || '').trim().toUpperCase());
    const f1 = (f || '').split(' ')[0]; return l && l.split(/\s+/).includes(last) && (f1 === first || (f1.length >= 4 && first.length >= 4 && f1.slice(0, 4) === first.slice(0, 4))); });
  if (!emp) return null;
  const p = pickDay(pj.people[emp], iso); if (!p) return null;
  return { ...homeOf(p.duc), how: p.exact ? 'payroll' : 'payroll~' };
}
